// hooks/useAddCourseSearch.ts
/**
 * Encapsulates course search logic for AddCourseModal:
 * - Debounced search with API calls
 * - Semester and offering pattern filters
 * - Course-in-plan and bookmarked status checks
 * - Auto-reset on modal open
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { typedApi } from '../services/typedApiClient';

interface AcademicPlan {
  semesters?: Record<string, { courses?: Array<{ code: string }> }>;
  bookmarkedCourses?: Array<{ code: string } | string>;
}

interface CourseResult {
  id?: number;
  code: string;
  title: string;
  credits: number;
  offering_pattern?: Record<string, boolean>;
}

interface UseAddCourseSearchOptions {
  isOpen: boolean;
  mode: string;
  academicPlan?: AcademicPlan;
}

export function useAddCourseSearch({ isOpen, mode, academicPlan }: UseAddCourseSearchOptions) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CourseResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [semesterFilter, setSemesterFilter] = useState<string | null>(null);
  const [patternFilter, setPatternFilter] = useState('all');

  const hasActiveFilters = semesterFilter != null || patternFilter !== 'all';

  const clearFilters = useCallback(() => {
    setSemesterFilter(null);
    setPatternFilter('all');
  }, []);

  // Reset search state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setShowFilters(false);
      setSemesterFilter(null);
      setPatternFilter('all');
    }
  }, [isOpen]);

  // Perform search API call
  const performSearch = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const params: Record<string, unknown> = {
        search: query,
        show_availability: true,
        limit: 20,
        ...(semesterFilter && { semester: semesterFilter }),
        ...(patternFilter !== 'all' && { pattern: patternFilter }),
      };

      const response = await (typedApi as any).courses.search(params);
      setSearchResults(response.data?.courses || response.courses || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [semesterFilter, patternFilter]);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mode === 'search') {
        performSearch(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, mode, performSearch]);

  // Re-search when filters change
  useEffect(() => {
    if (mode === 'search' && searchQuery.length >= 2) {
      performSearch(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semesterFilter, patternFilter]);

  // Course status checks
  const isCourseInPlan = useCallback((courseCode: string): boolean => {
    if (!academicPlan?.semesters) return false;
    for (const semester of Object.values(academicPlan.semesters)) {
      if (semester.courses?.some((c: { code: string }) => c.code === courseCode)) {
        return true;
      }
    }
    return false;
  }, [academicPlan]);

  const isCourseBookmarked = useCallback((courseCode: string): boolean => {
    if (!academicPlan?.bookmarkedCourses) return false;
    return academicPlan.bookmarkedCourses.some((b: { code: string } | string) =>
      (typeof b === 'object' && b.code === courseCode) || (b === courseCode)
    );
  }, [academicPlan]);

  return useMemo(() => ({
    // Search state
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    // Filter state
    showFilters,
    setShowFilters,
    semesterFilter,
    setSemesterFilter,
    patternFilter,
    setPatternFilter,
    hasActiveFilters,
    clearFilters,
    // Course status checks
    isCourseInPlan,
    isCourseBookmarked,
  }), [
    searchQuery, searchResults, searchLoading,
    showFilters, semesterFilter, patternFilter, hasActiveFilters,
    clearFilters, isCourseInPlan, isCourseBookmarked,
  ]);
}
